import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Copy, Loader2, Upload, QrCode, CheckCircle2, ArrowLeft } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import api, { API, formatApiError } from "@/lib/api";
import { brl, pct, maskWhatsapp, imgVersion } from "@/lib/format";
import ShareCard from "@/components/ShareCard";
import BankAccounts from "@/components/BankAccounts";

const PRESETS = [50, 100, 200, 500, 1000];

function parseValor(v) {
  return parseFloat(String(v).replace(/\./g, "").replace(",", ".")) || 0;
}

export default function ContributeModal({ meta, config, open, onClose, onSubmitted }) {
  const [step, setStep] = useState("select"); // select | form | done
  const [preset, setPreset] = useState(null);
  const [customOpen, setCustomOpen] = useState(false);
  const [customValor, setCustomValor] = useState("");
  const [nome, setNome] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [valorPago, setValorPago] = useState("");
  const [oracao, setOracao] = useState("");
  const [anonimo, setAnonimo] = useState(false);
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [sentInfo, setSentInfo] = useState({ comprovante: false, oracao: false });

  const restante = meta.valor_restante;
  const valorSelecionado = customOpen ? parseValor(customValor) : preset;
  const valorValido = valorSelecionado && valorSelecionado > 0 && valorSelecionado <= restante;

  const copyPix = async () => {
    try {
      await navigator.clipboard.writeText(meta.chave_pix);
      toast.success("Chave Pix copiada!");
    } catch (e) {
      toast.error("Não foi possível copiar. Selecione e copie manualmente.");
    }
  };

  const pickPreset = (v) => {
    setPreset(v);
    setCustomOpen(false);
    setCustomValor("");
  };

  const pickRestante = () => pickPreset(restante);

  const goToForm = () => {
    if (!valorValido) {
      toast.error(restante <= 0 ? "Esta conta já foi paga." : "Escolha um valor até " + brl(restante) + ".");
      return;
    }
    setValorPago(String(valorSelecionado).replace(".", ","));
    setStep("form");
  };

  const submit = async (e) => {
    e.preventDefault();
    const valorNum = parseValor(valorPago);
    if (!nome.trim()) return toast.error("Informe seu nome.");
    if (!valorNum || valorNum <= 0) return toast.error("Informe o valor pago.");
    if (valorNum > restante) return toast.error("O valor é maior que o saldo restante (" + brl(restante) + ").");
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("meta_id", meta.id);
      fd.append("nome", nome.trim());
      fd.append("whatsapp", whatsapp);
      fd.append("valor", valorNum);
      fd.append("pedido_oracao", oracao);
      fd.append("anonimo", anonimo ? "true" : "false");
      if (file) fd.append("comprovante", file);
      await api.post("/contribuicoes", fd);
      setSentInfo({ comprovante: !!file, oracao: !!oracao.trim(), nome: nome.trim(), valor: valorNum });
      setStep("done");
      onSubmitted?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setStep("select");
    setPreset(null);
    setCustomOpen(false);
    setCustomValor("");
    setNome("");
    setWhatsapp("");
    setValorPago("");
    setOracao("");
    setAnonimo(false);
    setFile(null);
  };

  const close = () => {
    onClose();
    setTimeout(reset, 300);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent
        data-testid="contribution-modal"
        className="max-w-lg max-h-[92vh] overflow-y-auto rounded-2xl p-6 sm:p-8"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-bold text-slate-900 pr-8">
            {step === "done" ? "Contribuição enviada" : `Ajudar a pagar — ${meta.referente}`}
          </DialogTitle>
          {step !== "done" && (
            <DialogDescription className="text-base text-slate-600">
              Poucos cliques e você já abençoa esta obra.
            </DialogDescription>
          )}
        </DialogHeader>

        {step === "select" && (
          <div className="space-y-5">
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 sm:p-5">
              <div className="space-y-1.5">
                <div className="flex justify-between text-base">
                  <span className="text-slate-500">Valor total</span>
                  <span className="font-bold text-slate-800">{brl(meta.valor_total)}</span>
                </div>
                <div className="flex justify-between text-base">
                  <span className="text-slate-500">Já arrecadado</span>
                  <span className="font-bold text-emerald-700">{brl(meta.valor_arrecadado)}</span>
                </div>
                <div className="flex justify-between text-base">
                  <span className="text-slate-500">Faltam</span>
                  <span className="font-bold text-blue-900">{brl(restante)}</span>
                </div>
              </div>
              <div className="mt-3 h-3 w-full rounded-full bg-slate-200 overflow-hidden" data-testid="modal-progress-bar">
                <div className="h-full rounded-full bg-blue-700 progress-animate" style={{ width: `${meta.percentual}%` }} />
              </div>
              <p className="mt-1.5 text-sm font-semibold text-slate-600">{pct(meta.percentual)}% arrecadado</p>
            </div>

            <div>
              <p className="text-base font-semibold text-slate-700 mb-2">1. Quanto deseja contribuir?</p>
              <div className="grid grid-cols-3 gap-2">
                {PRESETS.map((v) => {
                  const disabled = v > restante;
                  const active = !customOpen && preset === v;
                  return (
                    <button
                      key={v}
                      type="button"
                      data-testid={`value-button-${v}`}
                      disabled={disabled}
                      onClick={() => pickPreset(v)}
                      className={`py-3.5 rounded-xl font-bold text-lg border transition-colors ${
                        active
                          ? "bg-blue-800 text-white border-blue-800 shadow-md"
                          : disabled
                            ? "bg-slate-50 text-slate-300 border-slate-200 cursor-not-allowed"
                            : "bg-white text-blue-900 border-slate-300 hover:border-blue-500 hover:bg-blue-50"
                      }`}
                    >
                      R$ {v}
                    </button>
                  );
                })}
                <button
                  type="button"
                  data-testid="value-button-outro"
                  onClick={() => {
                    setCustomOpen(true);
                    setPreset(null);
                  }}
                  className={`py-3.5 rounded-xl font-bold text-base border transition-colors ${
                    customOpen
                      ? "bg-blue-800 text-white border-blue-800 shadow-md"
                      : "bg-white text-blue-900 border-slate-300 hover:border-blue-500 hover:bg-blue-50"
                  }`}
                >
                  OUTRO VALOR
                </button>
              </div>
              {customOpen && (
                <Input
                  data-testid="custom-value-input"
                  value={customValor}
                  onChange={(e) => setCustomValor(e.target.value)}
                  placeholder={`Digite o valor (máx. ${brl(restante)})`}
                  inputMode="decimal"
                  className="mt-2 h-12 text-base rounded-xl"
                  autoFocus
                />
              )}
              <button
                type="button"
                data-testid="value-button-restante"
                onClick={pickRestante}
                className={`mt-2 w-full py-3.5 rounded-xl font-bold text-base border-2 transition-colors ${
                  !customOpen && preset === restante
                    ? "bg-emerald-600 text-white border-emerald-600 shadow-md"
                    : "bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100"
                }`}
              >
                PAGAR O VALOR RESTANTE ({brl(restante)})
              </button>
            </div>

            {meta.chave_pix && (
              <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 sm:p-5 text-center">
                <p className="text-base font-semibold text-slate-700 mb-3 text-left flex items-center justify-between">
                  <span>2. Faça o Pix</span>
                  <span data-testid="pix-mode-badge" className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${config?.pagamento?.modo === "automatico" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-white text-slate-500 border-slate-200"}`}>
                    {config?.pagamento?.modo === "automatico" ? "CONFIRMAÇÃO AUTOMÁTICA" : "CONFIRMAÇÃO MANUAL"}
                  </span>
                </p>
                <div className="flex justify-center">
                  <img
                    src={`${API}/metas/${meta.id}/qrcode.png${imgVersion(meta)}`}
                    alt="QR Code Pix"
                    data-testid="pix-qrcode-image"
                    className="w-64 h-64 sm:w-56 sm:h-56 rounded-2xl bg-white p-3 border border-slate-200 object-contain shadow-sm"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </div>
                <p className="mt-3 text-sm font-medium text-slate-500 flex items-center justify-center gap-1.5">
                  <QrCode className="w-4 h-4" /> Chave Pix
                </p>
                <p data-testid="pix-key-text" className="font-code text-base sm:text-lg font-bold text-blue-900 break-all mt-1">
                  {meta.chave_pix}
                </p>
                <button
                  type="button"
                  data-testid="copy-pix-button"
                  onClick={copyPix}
                  className="mt-3 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-blue-800 hover:bg-blue-700 text-white font-bold text-lg transition-colors"
                >
                  <Copy className="w-5 h-5" /> COPIAR CHAVE PIX
                </button>
                <p className="mt-3 text-sm text-slate-500">Faça o pagamento pelo aplicativo do seu banco.</p>
              </div>
            )}

            <BankAccounts contas={config?.contas_bancarias} />

            <p className="text-base font-semibold text-slate-700 -mb-3">3. Depois de pagar</p>
            <button
              type="button"
              data-testid="ja-paguei-button"
              onClick={goToForm}
              className="w-full py-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-lg shadow-md transition-colors"
            >
              JÁ FIZ O PAGAMENTO
            </button>
          </div>
        )}

        {step === "form" && (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="c-nome" className="text-base font-semibold text-slate-700">Nome *</Label>
              <Input
                id="c-nome"
                data-testid="input-contributor-name"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Ex.: Maria da Silva"
                className="mt-1.5 h-12 text-base rounded-xl"
              />
              {config?.mural_ativo && (
                <label className="mt-2 flex items-center justify-between gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 cursor-pointer">
                  <span className="text-sm text-slate-700">
                    <span className="font-semibold">Aparecer como "Anônimo"</span> no mural de quem já ajudou
                  </span>
                  <Switch data-testid="input-anonimo" checked={anonimo} onCheckedChange={setAnonimo} />
                </label>
              )}
            </div>
            <div>
              <Label htmlFor="c-zap" className="text-base font-semibold text-slate-700">WhatsApp</Label>
              <Input
                id="c-zap"
                data-testid="input-contributor-whatsapp"
                value={whatsapp}
                onChange={(e) => setWhatsapp(maskWhatsapp(e.target.value))}
                placeholder="(11) 99999-9999"
                inputMode="numeric"
                className="mt-1.5 h-12 text-base rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="c-valor" className="text-base font-semibold text-slate-700">Valor pago (R$) *</Label>
              <Input
                id="c-valor"
                data-testid="input-contribution-amount"
                value={valorPago}
                onChange={(e) => setValorPago(e.target.value)}
                placeholder="Ex.: 100,00"
                inputMode="decimal"
                className="mt-1.5 h-12 text-base rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="c-comp" className="text-base font-semibold text-slate-700">Enviar comprovante (JPG, PNG ou PDF · até 5 MB)</Label>
              <label
                htmlFor="c-comp"
                className="mt-1.5 flex items-center justify-center gap-2 h-12 rounded-xl border-2 border-dashed border-slate-300 hover:border-blue-400 text-slate-600 cursor-pointer transition-colors text-base"
              >
                <Upload className="w-5 h-5" />
                {file ? file.name : "Toque para anexar o comprovante"}
              </label>
              <input
                id="c-comp"
                data-testid="input-receipt-upload"
                type="file"
                accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  if (f && f.size > 5 * 1024 * 1024) { toast.error("Arquivo muito grande (máx. 5 MB)."); e.target.value = ""; return setFile(null); }
                  setFile(f);
                }}
              />
            </div>
            <div>
              <Label htmlFor="c-oracao" className="text-base font-semibold text-slate-700">Pedido de oração (opcional)</Label>
              <Textarea
                id="c-oracao"
                data-testid="input-prayer-request"
                value={oracao}
                onChange={(e) => setOracao(e.target.value)}
                placeholder="Escreva aqui seu pedido de oração."
                className="mt-1.5 text-base rounded-xl min-h-[130px]"
              />
            </div>
            <button
              type="submit"
              data-testid="submit-contribution-button"
              disabled={sending}
              className="w-full py-4 rounded-xl bg-blue-800 hover:bg-blue-700 disabled:opacity-60 text-white font-bold text-lg shadow-md transition-colors flex items-center justify-center gap-2"
            >
              {sending && <Loader2 className="w-5 h-5 animate-spin" />}
              {sending ? "Enviando..." : "ENVIAR"}
            </button>
            <button
              type="button"
              data-testid="back-to-select-button"
              onClick={() => setStep("select")}
              className="w-full py-2.5 rounded-xl text-slate-500 hover:text-blue-800 font-semibold text-base transition-colors inline-flex items-center justify-center gap-1.5"
            >
              <ArrowLeft className="w-4 h-4" /> Voltar
            </button>
            <p className="text-center text-sm text-slate-400">
              O valor só é descontado da meta após a confirmação da administração.
            </p>
          </form>
        )}

        {step === "done" && (
          <div data-testid="contribution-success" className="py-2 space-y-4">
            <div className="flex justify-center">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="w-10 h-10 text-emerald-600" />
              </div>
            </div>
            <div className="space-y-2 text-center">
              <p className="text-lg font-semibold text-slate-800">
                {sentInfo.comprovante ? "Recebemos seu comprovante." : "Recebemos sua contribuição."}
              </p>
              <p className="text-base text-slate-600">Seu pagamento será conferido.</p>
              {sentInfo.oracao && (
                <p className="text-base text-slate-600">Recebemos também seu pedido de oração.</p>
              )}
              <p data-testid="success-custom-message" className="text-base text-slate-500 pt-1">{config?.mensagem_pos_pagamento || "Deus abençoe sua generosidade!"}</p>
              {config?.whatsapp && (
                <p className="text-sm text-slate-500">Dúvidas? WhatsApp da igreja: <span className="font-semibold text-slate-700">{config.whatsapp}</span></p>
              )}
            </div>
            <ShareCard meta={meta} config={config} nome={sentInfo.nome} valor={sentInfo.valor} />
            <button
              type="button"
              data-testid="close-success-button"
              onClick={close}
              className="w-full py-4 rounded-xl bg-blue-800 hover:bg-blue-700 text-white font-bold text-lg shadow-md transition-colors"
            >
              FECHAR
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
